import type { QAQuestionDTO } from '@next-space/fe-api-idl';
import type { FC } from 'react';
import { memo, useEffect, useState } from 'react';
import { Element } from 'react-scroll';
import { cx } from '../../cx';
import { fastEqual } from '../../utils';
import { getScrollChatList } from '../const';
import { QuestionContext, useConversationContext } from '../hooks/context';
import { useChatStatus } from '../hooks/hook';
import { ChatStatus, ChatUIStyle, SideBarType } from '../hooks/types';
import { ChatItem } from './chat-item';
import { ChatItemSkeleton, LiteChatItemSkeleton } from './skeleton';
import { isMobile } from 'react-device-detect';
import '../style/custom-scroll.css';
import { setIgnoreScrollBottom } from '../hooks/store-tools';

interface QuestionListProps {
  questions: QAQuestionDTO[];
  className?: string;
}

export const QuestionList: FC<QuestionListProps> = memo((props) => {
  const { conversationId, style, sideBarType, isPersonalWiki } = useConversationContext();
  const { questions, className } = props;
  const status = useChatStatus(conversationId);

  const isCreating = status === ChatStatus.CREATING;
  const isLite = style === ChatUIStyle.LITE;

  return (
    <Element
      onWheelCapture={(e) => {
        if (
          e.currentTarget.scrollTop ===
          e.currentTarget.scrollHeight - e.currentTarget.clientHeight
        ) {
          setIgnoreScrollBottom(false);
        } else {
          //用户自己滚动了列表就不在自动滚动到底部了
          setIgnoreScrollBottom(true);
        }
      }}
      name={getScrollChatList(conversationId)}
      id={getScrollChatList(conversationId)}
      className={cx(
        'flex-1 overflow-y-auto pb-10 relative w-full chat-page-scroller',
        isLite ? 'space-y-6' : 'space-y-8',
        className,
        {
          'max-h-[calc(90vh-40px-110px-12px-40px)]': isPersonalWiki && !isMobile,
        }
      )}
      style={{ scrollBehavior: 'smooth' }}
    >
      {questions.map((item, index) => (
        <QuestionItem
          key={item.id}
          question={item}
          index={index}
          totalIndex={isCreating ? questions.length + 1 : questions.length}
        />
      ))}
      {isCreating &&
        (isLite ? (
          <LiteChatItemSkeleton />
        ) : (
          <ChatItemSkeleton showSideBar={sideBarType === SideBarType.OUTLINE && !isMobile} />
        ))}
    </Element>
  );
}, fastEqual);

interface QuestionItemProps {
  question: QAQuestionDTO;
  index: number;
  totalIndex: number;
}

const QuestionItem: FC<QuestionItemProps> = memo((props) => {
  const { question, index, totalIndex } = props;
  const { conversationId } = useConversationContext();
  const totalChats = question.chatList.length;
  const [currentIndex, setCurrentIndex] = useState(Math.max(0, totalChats - 1));
  const currentChat = question.chatList[currentIndex];

  const handlePrevious = () => {
    setCurrentIndex((prev) => Math.max(0, prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => Math.min(prev + 1, totalChats - 1));
  };

  useEffect(() => {
    setCurrentIndex(Math.max(0, totalChats - 1));
  }, [totalChats]);

  if (!currentChat) {
    return null;
  }

  return (
    <QuestionContext.Provider
      value={{
        conversationId,
        questionId: question.id,
        index,
        totalIndex,
        curChatIndex: currentIndex,
        totalChats,
        handlePrevious,
        handleNext,
      }}
    >
      <ChatItem key={currentChat.uuid} item={currentChat} />
    </QuestionContext.Provider>
  );
}, fastEqual);
